import { useState, useEffect, useMemo, useCallback } from "react";
import { useSettingsContext } from "../SettingsProvider";

type UseSettingsStateOptions<T> = {
  /** Path to extract from settings (e.g., "locations" or "visitDetails.typeOfVisitOptions") */
  settingsPath: string;
  /** Path to save back to settings (defaults to settingsPath) */
  savePath?: string;
  /** Default value if settings path returns undefined */
  defaultValue?: T;
};

type UseSettingsStateReturn<T> = {
  /** Current local state value */
  value: T;
  /** Update local state */
  setValue: React.Dispatch<React.SetStateAction<T>>;
  /** Whether the value has been modified from original */
  isDirty: boolean;
  /** Whether initial load is complete */
  isInitialized: boolean;
  /** Whether a save operation is in progress */
  isSaving: boolean;
  /** Whether the settings are loading */
  isLoading: boolean;
  /** Save the current value to settings */
  save: () => Promise<void>;
  /** Reset to original value */
  reset: () => void;
};

/**
 * Get a nested value from an object using dot notation
 */
const getNestedValue = (obj: any, path: string): any => {
  return path.split(".").reduce((current, key) => current?.[key], obj);
};

/**
 * Create a nested object from a path and value
 */
const createNestedObject = (path: string, value: any): any => {
  const keys = path.split(".");
  const result: any = {};
  let current = result;

  for (let i = 0; i < keys.length - 1; i++) {
    current[keys[i]] = {};
    current = current[keys[i]];
  }

  current[keys[keys.length - 1]] = value;
  return result;
};

/**
 * Hook for managing settings state with local editing and save functionality.
 * Provides dirty state tracking, save/reset operations, and loading states.
 */
export const useSettingsState = <T>({
  settingsPath,
  savePath,
  defaultValue = [] as unknown as T,
}: UseSettingsStateOptions<T>): UseSettingsStateReturn<T> => {
  const { settings, setSettings, isLoading } = useSettingsContext();

  // Memoize original value from settings
  const originalValue = useMemo(() => {
    const value = getNestedValue(settings, settingsPath);
    return (value ?? defaultValue) as T;
  }, [settings, settingsPath, defaultValue]);

  // Local state
  const [value, setValue] = useState<T>(defaultValue);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Sync local state when original value changes
  useEffect(() => {
    setValue(originalValue);
    setIsInitialized(true);
  }, [originalValue]);

  // Calculate if dirty
  const isDirty = useMemo(() => {
    if (!isInitialized) return false;
    return JSON.stringify(value) !== JSON.stringify(originalValue);
  }, [value, originalValue, isInitialized]);

  // Save function
  const save = useCallback(async () => {
    setIsSaving(true);
    try {
      const saveObject = createNestedObject(savePath || settingsPath, value);
      await setSettings(saveObject);
    } finally {
      setIsSaving(false);
    }
  }, [value, savePath, settingsPath, setSettings]);

  // Reset function
  const reset = useCallback(() => {
    setValue(originalValue);
  }, [originalValue]);

  return {
    value,
    setValue,
    isDirty,
    isInitialized,
    isSaving,
    isLoading,
    save,
    reset,
  };
};

